import { useCallback, useState } from "react";
import type { U3134TecAgeRequest } from "./U3134Service";
import { u3134CanExportTecAge } from "./u3134TecAgeExportGuard";

export interface UseU3134TecAgeExportHandlersProps {
  doExportTecAgeExcelFn: (body: U3134TecAgeRequest) => Promise<unknown>;
  doExportTecAgePdfFn: (body: U3134TecAgeRequest) => Promise<unknown>;
  onExcelSuccess: () => void;
  onPdfSuccess: () => void;
  onError: (params: { error: unknown }) => void;
}

export function useU3134TecAgeExportHandlers({
  doExportTecAgeExcelFn,
  doExportTecAgePdfFn,
  onExcelSuccess,
  onPdfSuccess,
  onError,
}: UseU3134TecAgeExportHandlersProps) {
  const [isExportingExcel, setIsExportingExcel] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);

  const exportExcel = useCallback(
    async (body: U3134TecAgeRequest) => {
      if (!u3134CanExportTecAge(body)) return;
      setIsExportingExcel(true);
      try {
        await doExportTecAgeExcelFn(body);
        onExcelSuccess();
      } catch (error: unknown) {
        onError({ error });
      } finally {
        setIsExportingExcel(false);
      }
    },
    [doExportTecAgeExcelFn, onExcelSuccess, onError],
  );

  const exportPdf = useCallback(
    async (body: U3134TecAgeRequest) => {
      if (!u3134CanExportTecAge(body)) return;
      setIsExportingPdf(true);
      try {
        await doExportTecAgePdfFn(body);
        onPdfSuccess();
      } catch (error: unknown) {
        onError({ error });
      } finally {
        setIsExportingPdf(false);
      }
    },
    [doExportTecAgePdfFn, onPdfSuccess, onError],
  );

  return { exportExcel, exportPdf, isExportingExcel, isExportingPdf };
}
